import type {
  ApiError,
  AsyncData,
  ServiceList,
  ServicesById,
  UserPreferencesServiceList,
  UserServiceList,
  WindowApi,
} from "../shared/types.js";
import { createCustomEvent } from "../internal/customEvent.js";
import { getCookie } from "../internal/cookieUtils.js";
import { getCCSAvailable, getCCSInitialError, getCCSIsLoading, getInitialUserServiceListFromWindow } from "./host.js";
import type {
  OnCurrentUserServiceListChanged,
  UserSettingsCookie,
  ServiceListSubscriber,
  AddServiceIdToFavoriteServiceIdListError,
  FavoriteServiceData,
} from "./types.js";
import {
  SERVICE_LIST_UPDATED_EVENT,
  SERVICE_LIST_ERROR_EVENT,
  ADD_SERVICE_ID_TO_FAVORITE_SERVICE_ID_LIST_EVENT,
  ADD_SERVICE_ID_TO_FAVORITE_SERVICE_ID_LIST_ERROR_EVENT,
} from "./types.js";

declare const window: WindowApi;

const subscribers = new Set<ServiceListSubscriber>();

const getAsyncDataList = (
  data: AsyncData<ServiceList>["data"],
  isLoading = false,
  error?: AsyncData<ServiceList>["error"]
): AsyncData<ServiceList> => ({
  data,
  isLoading,
  error,
  isError: !!error,
});

const parseCookieJson = (userInfoCookieJson: string | null): unknown => {
  if (userInfoCookieJson) {
    try {
      return JSON.parse(userInfoCookieJson);
    } catch (e) {
      // do nothing - treat as though no user info stored in cookie
      return {};
    }
  } else {
    return {};
  }
};

export const parseUserSettingsCookie = () => {
  const cookieJson = getCookie("noflush_awscnm");
  return parseCookieJson(cookieJson) as UserSettingsCookie;
};

const sortRecentlyVisitedList = (recentlyVisitedIds: ServiceList = []) => {
  return recentlyVisitedIds.reverse();
};

const filterUnlistedRecentlyVisitedList = (services: string[] = [], servicesById: ServicesById = {}) => {
  return services.filter((serviceId) => {
    if (servicesById[serviceId]) {
      const { unlisted, history } = servicesById[serviceId];
      // unlisted: false should be part of Recently visited
      // Unlisted: true and history: true should be part of Recently visited
      return !unlisted || (unlisted && history);
    }
    return false;
  });
};

const filterUnlistedFavoriteList = (services: string[] = [], servicesById: ServicesById = {}) => {
  return services.filter((serviceId) => servicesById[serviceId] && !servicesById[serviceId].unlisted);
};

const getInitialUserServiceList = (): UserServiceList => {
  const initialUserServiceList: UserServiceList = {};
  let ccsLoadingStatus = false;
  let ccsInitialError: ApiError | undefined;
  let recentlyVisitedServiceIdList: ServiceList | undefined = [];
  let favoriteServiceIdList: ServiceList | undefined = [];

  if (getCCSAvailable()) {
    const initialUserServiceListFromWindow = getInitialUserServiceListFromWindow();
    recentlyVisitedServiceIdList = initialUserServiceListFromWindow?.recentlyVisitedServiceIdList;
    favoriteServiceIdList = initialUserServiceListFromWindow?.favoriteServiceIdList;

    ccsLoadingStatus = getCCSIsLoading();
    ccsInitialError = getCCSInitialError();
  } else {
    // Read from the cookie
    const { hist, sc } = parseUserSettingsCookie();
    recentlyVisitedServiceIdList = sortRecentlyVisitedList(hist);
    favoriteServiceIdList = sc;
    ccsInitialError = undefined;
  }
  const servicesById = window.ConsoleNavService?.Model?.servicesById;

  const filteredRecentlyVisitedList = filterUnlistedRecentlyVisitedList(recentlyVisitedServiceIdList, servicesById);
  const filteredFavoriteList = filterUnlistedFavoriteList(favoriteServiceIdList, servicesById);

  initialUserServiceList.recentlyVisited = getAsyncDataList(
    filteredRecentlyVisitedList,
    ccsLoadingStatus,
    ccsInitialError
  );
  initialUserServiceList.favorite = getAsyncDataList(filteredFavoriteList, ccsLoadingStatus, ccsInitialError);

  return initialUserServiceList;
};

export const onServiceListUpdated = ({ detail }: CustomEvent<Partial<UserPreferencesServiceList>>) => {
  const servicesById = window.ConsoleNavService?.Model?.servicesById;

  const filteredRecentlyVisitedList = detail?.recentlyVisitedServiceIdList
    ? filterUnlistedRecentlyVisitedList(detail.recentlyVisitedServiceIdList, servicesById)
    : null;
  const filteredFavoriteList = detail?.favoriteServiceIdList
    ? filterUnlistedFavoriteList(detail.favoriteServiceIdList, servicesById)
    : null;

  for (const subscriber of subscribers) {
    subscriber({
      ...(filteredFavoriteList && { favorite: getAsyncDataList(filteredFavoriteList) }),
      ...(filteredRecentlyVisitedList && {
        recentlyVisited: getAsyncDataList(filteredRecentlyVisitedList),
      }),
    });
  }
};

export const onServiceListError = ({ detail }: CustomEvent<ApiError>) => {
  const servicesById = window.ConsoleNavService?.Model?.servicesById;
  const initialUserServiceListFromWindow = getInitialUserServiceListFromWindow();
  const recentlyVisitedServiceIdList = initialUserServiceListFromWindow?.recentlyVisitedServiceIdList;
  const favoriteServiceIdList = initialUserServiceListFromWindow?.favoriteServiceIdList;
  const filteredRecentlyVisitedList = filterUnlistedRecentlyVisitedList(recentlyVisitedServiceIdList, servicesById);
  const filteredFavoriteList = filterUnlistedFavoriteList(favoriteServiceIdList, servicesById);
  for (const subscriber of subscribers) {
    subscriber({
      recentlyVisited: getAsyncDataList(filteredRecentlyVisitedList, false, detail),
      favorite: getAsyncDataList(filteredFavoriteList, false, detail),
    });
  }
};

const listenToServiceListUpdatedEvent = () => {
  window.addEventListener(SERVICE_LIST_UPDATED_EVENT, onServiceListUpdated as EventListenerOrEventListenerObject);
};

const listenToServiceListErrorEvent = () => {
  window.addEventListener(SERVICE_LIST_ERROR_EVENT, onServiceListError as EventListenerOrEventListenerObject);
};

export const onCurrentUserServiceListChanged = (subscriber: ServiceListSubscriber): OnCurrentUserServiceListChanged => {
  subscribers.add(subscriber);

  return {
    unsubscribe: () => subscribers.delete(subscriber),
    initialUserServiceList: getInitialUserServiceList(),
  };
};

/**
 * Exposed for testing purposes. You should not call this function directly outside of your unit tests.
 */
export const updateDataToSubscribers = (data: Partial<UserPreferencesServiceList>) => {
  for (const subscriber of subscribers) {
    subscriber({
      ...(data.favoriteServiceIdList && { favorite: getAsyncDataList(data.favoriteServiceIdList) }),
      ...(data.recentlyVisitedServiceIdList && {
        recentlyVisited: getAsyncDataList(data.recentlyVisitedServiceIdList),
      }),
    });
  }
};

export const dispatchAddServiceIdToFavoriteServiceIdListEvent = ({
  serviceId,
  favoriteServiceSource,
}: FavoriteServiceData) => {
  const event = createCustomEvent(ADD_SERVICE_ID_TO_FAVORITE_SERVICE_ID_LIST_EVENT, {
    detail: { serviceId, favoriteServiceSource },
  });
  window.dispatchEvent(event);
};

export const listenToAddServiceIdToFavoriteServiceIdListErrorEvent = (
  listener: (error: CustomEvent<AddServiceIdToFavoriteServiceIdListError>) => void
) => {
  window.addEventListener(
    ADD_SERVICE_ID_TO_FAVORITE_SERVICE_ID_LIST_ERROR_EVENT,
    listener as EventListenerOrEventListenerObject
  );
};

listenToServiceListUpdatedEvent();
listenToServiceListErrorEvent();
